import { Component, EventEmitter, Input, OnInit, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { util } from '../../../classes/util';
import { ESRI } from '../../../esri-loader-util';
import { BooleanConverter, InputConverter } from '../../../inputConverter';
import { LayerService } from '../../../services/layer.service';
import { IMapcoreFeatureLayer, IMCMapView } from '../../../types';
import { BaseComponent } from '../../base.component';
import { MapComponent } from '../../mapComponents/map/map.component';

let Graphic: __esri.GraphicConstructor;
let PopupConstructor: __esri.PopupConstructor;

interface IOpeningEvent {
    cancel: boolean;
}

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-layer-tooltip-ng',
    styleUrls: ['../../sharedStyles/base.scss', './layer-tooltip.component.scss'],
    templateUrl: './layer-tooltip.component.html'
})
export class LayerTooltipComponent extends BaseComponent implements OnInit {
    @Input()
    public get tooltipEnabled(): boolean {
        return this._tooltipEnabled;
    }
    public set tooltipEnabled(value: boolean) {
        this._tooltipEnabled = BooleanConverter(value);
        if (this.tooltipEnabled) {
            this.enableTooltip();
        } else {
            this.disableTooltip();
        }
    }
    private _tooltipEnabled: boolean;

    @Input()
    public highlightColor: string = 'rgba(125, 247, 229, 0.75)';

    @Input()
    @InputConverter(BooleanConverter)
    public highlightEnabled = false;

    @Output()
    public readonly opening = new EventEmitter<IOpeningEvent>();

    highlightLayer: __esri.GraphicsLayer;
    mapView: IMCMapView;
    mapRef: MapComponent;
    mapPointerMoveHandler: IHandle;
    popupRef: __esri.Popup;
    popupVisibleWatcher: __esri.WatchHandle;
    popupSelectedFeatureWatcher: __esri.WatchHandle;
    timeoutId: number;

    constructor(_vcr: ViewContainerRef,
                private readonly layerService: LayerService) { super(_vcr); }

    async ngOnInit() {
        try {
            this.mapRef = await this.getClosestAncestor('mc-map');
            await this.mapRef.ready;
            this.mapView = this.mapRef.mapView;
            this.tooltipEnabled = this.tooltipEnabled === undefined ? (this.mapView.mapcoreViewProps && this.mapView.mapcoreViewProps.tooltip ? true : false) : this.tooltipEnabled;
            this.mapView.popup.autoOpenEnabled = false;
            [Graphic, PopupConstructor] = await Promise.all([ESRI.Graphic, ESRI.Popup]);
        } catch (error) {
            this.readyProblem(error);
        } finally {
            this.isReady();
        }
    }

    async enableTooltip() {
        await this.ready;
        if (!this.popupRef) {
            this.mapView.popup = new PopupConstructor({
                autoOpenEnabled: false,
                defaultPopupTemplateEnabled: true
            });

            this.popupRef = this.mapView.popup;
            this.popupVisibleWatcher = this.popupRef.watch('visible', () => {
                if (!this.popupRef.visible) {
                    if (this.highlightEnabled) {
                        this.highlightLayer.removeAll();
                    }

                    this.mouseInside = false;
                }
            });

            if (this.highlightEnabled) {
                this.highlightLayer = await this.layerService.addGraphicsLayer({
                    title: 'Tooltip Highlight',
                    hideInLegend: true
                });

                this.popupSelectedFeatureWatcher = this.popupRef.watch('selectedFeature', g => {
                    this.highlightGraphic(g);
                });
            }

            const containerWatch = this.popupRef.watch('container', () => {
                const container = this.popupRef.container as HTMLElement;
                container.addEventListener('mouseenter', this.mouseEnterHandler.bind(this));
                container.addEventListener('mouseleave', this.mouseLeaveHandler.bind(this));
                containerWatch.remove();
            });

            const waitTime = 500;

            const pointerDebounce = util.debounce((e) => {
                this.clearWorkTimeout();
                if (!this.mouseInside) {
                    this.timeoutId = window.setTimeout(this.work.bind(this, e), waitTime);
                }
            }, waitTime);

            this.mapPointerMoveHandler = this.mapView.on('pointer-move', (e) => {
                if (this.popupRef && this._tooltipEnabled) {
                    pointerDebounce.call(this, e);
                }
            });
        }

    }

    private clearWorkTimeout() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
    }

    private async work(args: __esri.ViewPointerMoveEvent) {
        const mapPoint = this.mapView.toMap(args);
        const screenPoint = this.mapView.toScreen(mapPoint);
        const hasGraphic = await this.hasGraphicFromGraphicsLayer(screenPoint);
        const layerHasId = layerId => layer => layer.mapcoreProps && layer.mapcoreProps.idFromSavedLayer === layerId;

        if (this.popupRef) {
            this.popupRef.fetchFeatures(screenPoint).then((response) => {
                if (!this.popupRef) {
                    return;
                }

                response.allGraphicsPromise.then((graphics) => {
                    const event = { cancel: false };
                    this.opening.emit(event);

                    if (event.cancel) {
                        return;
                    }

                    const anyLayerHoverEnabled = (<Array<any>>graphics).some(g =>
                        g.sourceLayer
                        && g.sourceLayer.mapcoreProps
                        && g.sourceLayer.mapcoreProps.hoverEnabled !== false);

                    if ((graphics.length === 0 && !hasGraphic)
                        || !anyLayerHoverEnabled) {
                        return;
                    }

                    // Sort graphics to match the order of layers on the map
                    graphics.sort((first: any, second: any) => {
                        const firstId = first.sourceLayer.mapcoreProps.idFromSavedLayer;
                        const secondId = second.sourceLayer.mapcoreProps.idFromSavedLayer;

                        const mapLayers = (<Array<IMapcoreFeatureLayer>>this.mapView.map.allLayers.toArray());
                        const firstIndex = mapLayers.findIndex(layerHasId(firstId));
                        const secondIndex = mapLayers.findIndex(layerHasId(secondId));

                        return secondIndex - firstIndex;
                    });

                    if (this.highlightEnabled) {
                        this.highlightGraphic(graphics[0]);
                    }

                    this.popupRef.open({
                        location: mapPoint,
                        features: graphics
                    });
                });
            });
        }
    }
    private highlightGraphic(feature: __esri.Graphic) {
        let highlightSymbol;

        if (feature.symbol) {
            highlightSymbol = { ...feature.symbol };
        } else {
            switch (feature.geometry.type) {
                case 'point':
                    highlightSymbol = { type: 'simple-marker', outline: { color: this.highlightColor } };
                    break;

                case 'polyline':
                    highlightSymbol = { type: 'simple-line', width: 3 };
                    break;

                case 'polygon':
                    highlightSymbol = { type: 'simple-fill' };
                    break;

                default:
                    highlightSymbol = { type: 'simple-marker' };
                    break;
            }
        }

        highlightSymbol.color = this.highlightColor;

        this.highlightLayer.removeAll();
        this.highlightLayer.add(new Graphic({
            geometry: feature.geometry,
            symbol: highlightSymbol
        }));
    }

    private mouseInside = false;
    private mouseEnterHandler() {
        this.clearWorkTimeout();
        this.mouseInside = true;
    }
    private mouseLeaveHandler() {
        this.mouseInside = false;
    }

    async hasGraphicFromGraphicsLayer(event) {
        const response = await this.mapView.hitTest(event);
        if (response.results.length) {
            response.results.forEach(result => {
                const graphic: __esri.Graphic = result.graphic;
                if (graphic.layer.type === 'graphics' && graphic.geometry.type === 'point') {
                    return true;
                }
            });
        } else {
            return false;
        }
    }

    @Input()
    closeTooltip = () => {
        if (this.popupRef) {
            this.popupRef.close();
            this.highlightLayer.removeAll();
        }
    }

    async disableTooltip() {
        if (this.popupRef) {
            if (this.popupRef.visible) {
                if (this.popupSelectedFeatureWatcher) {
                    this.popupSelectedFeatureWatcher.remove();
                }

                this.popupVisibleWatcher.remove();
                this.popupRef.close();
            }

            if (this.highlightLayer) {
                this.mapRef.map.layers.remove(this.highlightLayer);
                this.highlightLayer = null;
            }

            const container = this.popupRef.container as HTMLElement;
            container.removeEventListener('mouseenter', this.mouseEnterHandler.bind(this));
            container.removeEventListener('mouseleave', this.mouseLeaveHandler.bind(this));
            this.mouseInside = false;
            this.popupRef = undefined;
        }
        if (this.mapView && this.mapView.mapcoreViewProps) {
            this.mapView.mapcoreViewProps.tooltip = false;
        }
        if (this.mapPointerMoveHandler) {
            this.mapPointerMoveHandler.remove();
            this.mapPointerMoveHandler = undefined;
        }
    }
}
